import React from 'react';
import { GripVertical, Video, FileText, ImageIcon, File, Eye, Pencil, Trash2 } from 'lucide-react';
import type { CourseLesson } from '@/services/creatorApi';

interface LessonItemProps {
  lesson: CourseLesson;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  /** Opens the inline video edit drawer — only rendered for video lessons with an s3Key. */
  onEditVideo?: () => void;
  dragHandleProps?: any;
}

const fileTypeIcon = (ft: string) => {
  switch (ft) {
    case 'pdf':
      return <FileText className="w-4 h-4" style={{ color: 'hsl(0 70% 60%)' }} />;
    case 'image':
      // --hj-blue is the brand yellow — unreadable on the white lesson row.
      // Match the file/video icons below (dark ink).
      return <ImageIcon className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-2)' }} />;
    case 'file':
      return <File className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-2)' }} />;
    default:
      return <Video className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-2)' }} />;
  }
};

const iconBtn: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 999,
  background: 'transparent',
  border: 0,
  color: 'var(--hj-beige-ink-3)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const pill = (label: React.ReactNode, tone: 'free' | 'drip'): React.ReactElement => {
  const palette = tone === 'free'
    ? { bg: 'var(--hj-blue-tint)', fg: 'var(--hj-beige-ink)', border: 'var(--hj-blue)' }
    : { bg: 'var(--hj-beige-hover)', fg: 'var(--hj-beige-ink-2)', border: 'var(--hj-beige-line)' };
  return (
    <span
      className="mono-label inline-flex items-center gap-1 flex-shrink-0"
      style={{
        padding: '2px 7px',
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
      }}
    >
      {label}
    </span>
  );
};

const LessonItem: React.FC<LessonItemProps> = ({ lesson, index, onEdit, onDelete, onEditVideo, dragHandleProps }) => {
  return (
    <div
      className="flex items-center gap-2 group transition-colors"
      style={{
        padding: '8px 10px',
        borderRadius: 'var(--hj-r-sm)',
      }}
    >
      <div
        {...dragHandleProps}
        className="cursor-grab active:cursor-grabbing"
        style={{ color: 'var(--hj-beige-ink-3)' }}
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <span
        className="tabular flex-shrink-0"
        style={{
          width: 22,
          textAlign: 'right',
          fontSize: 11.5,
          color: 'var(--hj-beige-ink-3)',
          fontFamily: 'var(--hj-font-mono)',
        }}
      >
        {index + 1}
      </span>
      {fileTypeIcon(lesson.fileType)}
      <span
        className="flex-1 truncate"
        style={{ fontSize: 13, color: 'var(--hj-beige-ink)' }}
      >
        {lesson.title || 'Untitled lesson'}
      </span>
      {lesson.freePreview && pill(
        <>
          <Eye className="w-3 h-3" /> Free
        </>,
        'free'
      )}
      {lesson.availableDate
        ? pill(<>{new Date(lesson.availableDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</>, 'drip')
        : lesson.dripDay > 0 && pill(<>Day {lesson.dripDay}</>, 'drip')}
      {lesson.fileType === 'video' && lesson.s3Key && (() => {
        const status = lesson.muxIngestStatus;
        if (!status) return null;
        const text =
          status === 'ready' ? 'Ready'
          : status === 'errored' ? 'Video failed'
          : status === 'deleted' ? 'Removed'
          : 'Processing…';
        const tone =
          status === 'ready' ? { bg: 'rgb(140 198 63 / 0.18)', fg: 'oklch(0.45 0.15 130)', border: 'rgb(140 198 63 / 0.45)' }
          : status === 'errored' ? { bg: 'rgb(220 70 70 / 0.14)', fg: 'oklch(0.42 0.16 25)', border: 'rgb(220 70 70 / 0.45)' }
          : { bg: 'var(--hj-beige-deep)', fg: 'var(--hj-beige-ink-3)', border: 'var(--hj-beige-line)' };
        return (
          <span
            className="mono-label inline-flex items-center flex-shrink-0"
            title={lesson.muxIngestError || undefined}
            style={{
              padding: '2px 7px',
              borderRadius: 999,
              background: tone.bg,
              color: tone.fg,
              border: `1px solid ${tone.border}`,
            }}
          >
            {text}
          </span>
        );
      })()}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex-shrink-0">
        {onEditVideo && lesson.fileType === 'video' && !!lesson.s3Key && (
          <button
            type="button"
            onClick={onEditVideo}
            style={iconBtn}
            aria-label={`Edit video: ${lesson.title || 'untitled'}`}
            title="Edit video"
          >
            <Video className="w-3.5 h-3.5" />
          </button>
        )}
        <button type="button" onClick={onEdit} style={iconBtn} aria-label={`Edit lesson: ${lesson.title || 'untitled'}`}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          style={{ ...iconBtn, color: 'hsl(var(--destructive))' }}
          aria-label={`Delete lesson: ${lesson.title || 'untitled'}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default LessonItem;
